import { seriesColor } from '@/components/charts/palette';
import { formatShare } from '@/lib/format';

export interface DonutSlice {
  key: string;
  label: string;
  /** The exact figure from the API. */
  display: string;
  /** Parsed value — arc length and share only. */
  value: number;
}

export interface DonutChartProps {
  slices: readonly DonutSlice[];
  /** Accessible name, e.g. "Sales by payment method". */
  label: string;
  /** Centre caption (exact total string) and its description. */
  centerValue: string;
  centerLabel: string;
}

const RADIUS = 56;
const STROKE = 18;
const SIZE = (RADIUS + STROKE / 2) * 2 + 4;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Composition donut with a legend carrying the exact figures. */
export function DonutChart({ slices, label, centerValue, centerLabel }: DonutChartProps) {
  const total = slices.reduce((sum, slice) => sum + Math.max(0, slice.value), 0);
  let offset = 0;

  const arcs = slices.map((slice, index) => {
    const share = total > 0 ? Math.max(0, slice.value) / total : 0;
    const length = share * CIRCUMFERENCE;
    const arc = { slice, index, length, offset };
    offset += length;
    return arc;
  });

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
      <div className="relative shrink-0">
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={label}>
          <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
            <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth={STROKE} />
            {total > 0
              ? arcs.map(({ slice, index, length, offset: start }) => (
                  <circle
                    key={slice.key}
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RADIUS}
                    fill="none"
                    stroke={seriesColor(index)}
                    strokeWidth={STROKE}
                    strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
                    strokeDashoffset={-start}
                  />
                ))
              : null}
          </g>
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="max-w-[92px] break-words text-sm font-semibold tabular-nums text-slate-900">
            {centerValue}
          </span>
          <span className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">{centerLabel}</span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-2">
        {slices.map((slice, index) => (
          <li key={slice.key} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-baseline gap-2">
              <span
                aria-hidden="true"
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: seriesColor(index) }}
              />
              <span className="truncate text-slate-700" title={slice.label}>
                {slice.label}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="font-medium tabular-nums text-slate-900">{slice.display}</span>
              <span className="ml-2 text-xs tabular-nums text-slate-400">{formatShare(slice.value, total)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
